import { useMemo, useState } from 'react';
import { requests, sourcing } from '../api/endpoints';
import type { FulfillmentOption, RequestEntity, RequestItem } from '../api/types';
import { Card, ErrorBox, fmtDate, KV, Loading, PageHeader, StatusBadge, Tabs } from '../components/ui';
import { navigate, useAsync } from '../hooks';
import { ActsClosureTab } from './request-card/ActsClosureTab';
import { CalculationsTab } from './request-card/CalculationsTab';
import { CommentsTab } from './request-card/CommentsTab';
import { DocumentsTab } from './request-card/DocumentsTab';
import { EventsTab } from './request-card/EventsTab';
import { ExtractionTab } from './request-card/ExtractionTab';
import { FilesTab } from './request-card/FilesTab';
import { ItemsTab } from './request-card/ItemsTab';
import { LaunchTab } from './request-card/LaunchTab';
import { MessagesTab } from './request-card/MessagesTab';
import { OffersTab } from './request-card/OffersTab';
import { OverviewTab } from './request-card/OverviewTab';
import { ProductsTab } from './request-card/ProductsTab';

export interface CardData {
  request: RequestEntity;
  items: RequestItem[];
  options: FulfillmentOption[];
  reload: () => void;
}

const VALID_TABS = new Set([
  'overview', 'products', 'items', 'calculations', 'offers', 'launch',
  'events', 'comments', 'files', 'documents', 'messages', 'extraction', 'closure',
]);

/** Начальная вкладка из hash-роuta '#/requests/<id>?tab=events'. */
function initialTabFromHash(): string | null {
  const query = window.location.hash.split('?')[1];
  const tab = query ? new URLSearchParams(query).get('tab') : null;
  return tab && VALID_TABS.has(tab) ? tab : null;
}

export function RequestCardPage({ requestId }: { requestId: string }) {
  const [tab, setTab] = useState(() => initialTabFromHash() ?? 'overview');

  const data = useAsync<CardData>(async () => {
    const request = await requests.get(requestId);
    const items = await requests.items(requestId);
    // Варианты исполнения грузим по каждой позиции (фильтр backend может не поддерживаться —
    // тогда дополнительно отфильтруем на клиенте).
    const optionLists = await Promise.all(items.map((it) => sourcing.options({ item: it.id }).catch(() => [] as FulfillmentOption[])));
    const itemIds = new Set(items.map((i) => i.id));
    const options = optionLists.flat().filter((o) => itemIds.has(o.item));
    const cardData: CardData = { request, items, options, reload: () => undefined };
    return cardData;
  }, [requestId]);

  const cardData = useMemo<CardData | null>(() => {
    if (!data.data) return null;
    return { ...data.data, reload: data.reload };
  }, [data.data, data.reload]);

  if (data.error && !cardData) {
    return (
      <div>
        <PageHeader title="Карточка заявки">
          <button className="btn" onClick={() => navigate('/requests')}>← К списку</button>
        </PageHeader>
        <ErrorBox error={data.error} onRetry={data.reload} />
      </div>
    );
  }

  if (!cardData) return <Loading text="Загрузка карточки заявки…" />;

  const { request } = cardData;
  const tabs = [
    { key: 'overview', title: 'Обзор и участники' },
    { key: 'products', title: 'Продукция' },
    { key: 'items', title: `Позиции и варианты (${cardData.items.length})` },
    { key: 'calculations', title: 'Расчёты' },
    { key: 'offers', title: 'Предложения' },
    { key: 'launch', title: 'Запуск и исполнение' },
    { key: 'events', title: 'События' },
    { key: 'comments', title: 'Комментарии' },
    { key: 'files', title: 'Файлы' },
    { key: 'documents', title: 'Документы' },
    { key: 'messages', title: 'Переписка' },
    { key: 'extraction', title: 'Извлечение' },
    { key: 'closure', title: 'Акты и закрытие' },
  ];

  return (
    <div>
      <PageHeader title={`Заявка ${request.number ?? ''}`}>
        <button className="btn" onClick={() => navigate('/requests')}>← К списку</button>
        <button className="btn" onClick={data.reload}>Обновить</button>
      </PageHeader>

      <Card>
        <div className="kv-grid">
          <KV k="Стадия" v={<StatusBadge value={request.stage} />} />
          <KV k="На паузе" v={request.on_hold ? `Да${request.hold_reason ? ` — ${request.hold_reason}` : ''}` : 'Нет'} />
          <KV k="Обещанный срок" v={fmtDate(request.promised_due_at)} />
          <KV k="Цикл просчёта" v={request.calculation_cycle ?? '—'} />
          <KV k="Создана" v={fmtDate(request.created_at)} />
          <KV k="Обновлена" v={fmtDate(request.updated_at)} />
          <KV k="Revision" v={request.revision ?? '—'} />
          <KV k="ID" v={<span className="muted">{request.id}</span>} />
        </div>
      </Card>

      <ErrorBox error={data.error} />

      <Tabs tabs={tabs} active={tab} onChange={setTab} />

      {tab === 'overview' && <OverviewTab data={cardData} onTabChange={setTab} />}
      {tab === 'products' && <ProductsTab data={cardData} />}
      {tab === 'items' && <ItemsTab data={cardData} />}
      {tab === 'calculations' && <CalculationsTab data={cardData} />}
      {tab === 'offers' && <OffersTab data={cardData} />}
      {tab === 'launch' && <LaunchTab data={cardData} />}
      {tab === 'events' && <EventsTab data={cardData} />}
      {tab === 'comments' && <CommentsTab data={cardData} />}
      {tab === 'files' && <FilesTab data={cardData} />}
      {tab === 'documents' && <DocumentsTab data={cardData} />}
      {tab === 'messages' && <MessagesTab data={cardData} />}
      {tab === 'extraction' && <ExtractionTab data={cardData} />}
      {tab === 'closure' && <ActsClosureTab data={cardData} />}
    </div>
  );
}
